import { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { callGetBadges } from "api/MuyeonApi";
import { onBadges } from "common/appSocket";
import { hasNativeBridge } from "util/userAgent";

/**
 * ★ 2026-10-31 하단 MY 탭 빨간 점·다른 유형 말풍선용 유형별 새 소식.
 *  서버 badges: categories(안 읽은 알림 분류별 수) + typeChips(가진 유형마다 보는 분류 = 알림함 칩과 같은 값) + quotes.
 *  - mine: 지금 유형이 보는 분류의 안 읽은 알림 + 견적 답장 안 읽음(MY '받은 견적' 점과 같은 값). 채팅은 플로팅 버튼에 점이 따로 있어 뺀다.
 *  - others: 다른 가진 유형마다 '그 유형에만 있는 분류'(지금 유형이 안 보는 분류)의 안 읽은 알림 — 공통 분류는 mine 에 이미 들어 있다.
 *  갱신: 브라우저는 소켓 'badges'(즉시), 앱 웹뷰는 웹 소켓을 열지 않으므로 화면 복귀·주소 이동·60초마다 다시 읽는다.
 */
export default function useTypeBadges(enabled, currentType) {
  const [data, setData] = useState(null);
  const location = useLocation();

  const load = useCallback(() => {
    if (!enabled) return;
    callGetBadges().then((r) => setData(r?.data || null)).catch(() => {});
  }, [enabled]);

  useEffect(() => {
    if (!enabled) { setData(null); return undefined; }
    const t = setInterval(load, 60000);
    const onVis = () => { if (document.visibilityState === "visible") load(); };
    document.addEventListener("visibilitychange", onVis);
    const off = hasNativeBridge() ? () => {} : onBadges((v) => { if (v && typeof v === "object") setData(v); });
    return () => { clearInterval(t); document.removeEventListener("visibilitychange", onVis); off(); };
  }, [enabled, load]);

  // 주소가 바뀔 때마다(알림을 읽고 돌아온 경우 포함) 다시 — 서버가 회원별 3초 캐시라 부담이 적다.
  useEffect(() => { load(); }, [location.pathname, load]);

  return useMemo(() => computeTypeBadges(data, currentType), [data, currentType]);
}

/** 서버 badges → { ready, mine, others } (위 규칙). 테스트용으로 따로 둔다. */
export function computeTypeBadges(data, currentType) {
  const categories = data?.categories || {};
  const chips = data?.typeChips || {};
  const sum = (keys) => (keys || []).reduce((n, k) => n + (Number(categories[k]) || 0), 0);
  const mineKeys = chips[currentType] || [];
  const mine = sum(mineKeys) + (Number(data?.quotes) || 0);
  const others = {};
  Object.keys(chips).forEach((t) => {
    if (t === currentType) return;
    const n = sum((chips[t] || []).filter((k) => !mineKeys.includes(k)));
    if (n > 0) others[t] = n;
  });
  return { ready: !!data, mine, others };
}
